import { Context } from 'Context/Context';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import AllCases from './AllCases';
import RequestedCases from './RequestedCases';
import { FIRM, STATE_SELECTION_ENABLED, filterOptionCountsApi, getCaseCountApi, listCasesApi, raiseNotificationApi, savedTeaserApi, userListCasesApi } from 'Constants';
import SortingOptions from './SortingOptions';
import { toast } from 'react-toastify';
import useIsMobile from 'Hooks/useIsMobile';
import { CaseListLayout, ResultLine, CardSkeleton, CasePager, CaseBtn } from './CaseListUI';
import BlurredSampleCases from './SampleCasesBlur';
import { FilterBar } from './CaseFilters';
import { CASE_TYPES, readCaseType, writeCaseType, useRestoreListScroll } from './CaseStage';
import FirmOverview from './FirmOverview';

// Available Cases (header menu) — case types in the left panel:
// SOS cases · Cases just posted (default) · Available cases · Requested cases
const TYPES = CASE_TYPES['1'];   // shared with the case page (CaseStage.jsx)
const OPEN_TYPE = { 0: 'openSos', 1: 'openLatest', 2: 'open' };
// Verified law firms get an "Overview" tab first (FirmOverview)
const OVERVIEW = 9;

// The saved default filters (case type + state) are merged in once per user per
// visit — after that the filters the user picks stay put across sections.
const defaultsMergedFor = new Set();

function OpenCases({attorney, setUpdateComp, updateComp})
{
    const {userDetails, makeApiCallFunc, filters, setFilters, reqRevCountArr, setReqRevCountArr} = useContext(Context);
    const userInfo = attorney ? attorney?.userId :  userDetails;
    const navigate = useNavigate();

    // free law firms can open every open-case list (they only ever see cases a
    // client saved their firm on), so they start on Cases just posted like Premium.
    // Law firms with no tab picked yet this visit land where their saved cases
    // are: SOS, then Cases just posted, then Available - else Cases just posted.
    // Verified law firms (with or without Premium) start on the Overview tab instead.
    const isFirmUser = userInfo?.role === FIRM;
    const overviewFirm = !attorney && isFirmUser && !!userInfo?.verified && !userDetails?.isUserAssistant;
    const [storedType] = useState(() => {
        const t = readCaseType('1', null);
        return t === OVERVIEW && !overviewFirm ? null : t; // Overview only exists for these firms
    });
    const pickLanding = isFirmUser && storedType === null && !overviewFirm;
    const [landingReady, setLandingReady] = useState(!pickLanding);
    const [innerPanel, setInnerPanel] = useState(() => storedType ?? (overviewFirm ? OVERVIEW : (userInfo.isActiveSubscription || userDetails?.role === FIRM) ? 1 : 2));

    const {offset, setOffset} = useContext(Context);
    const [sendCount, setSendCount] = useState(innerPanel !== 3);
    const [caseTypeCount, setCaseTypeCount] = useState(innerPanel !== 3 ? ["requested"] : []);
    const [isVerified] = useState(userInfo?.verified);

    const [limit] = useState(isVerified? 5 : 3);

    const [cases, setCases] = useState(null);
    const fetchSeq = useRef(0);   // ignore responses from superseded requests
    const [requestedCount, setRequestedCount] = useState(0);
    const [totalCases, setTotalCases] = useState(0);
    // Premium law firms: cases where a client saved their firm come first; the
    // "Saved your firm" chip shows only those. Free firms already see only those.
    const premiumFirm = userDetails?.role === 5 && !!userInfo?.isActiveSubscription;
    const [savedOnly, setSavedOnly] = useState(false);
    const [savedCount, setSavedCount] = useState(null);
    // Free law firms: how many cases in the locked SOS / just-posted lists saved their firm (shown in the upgrade popup)
    const [lockedSaved, setLockedSaved] = useState(null);
    const freeFirm = userDetails?.role === 5 && !!userDetails?.lawFirmId && !userInfo?.isActiveSubscription;
    // saved-case counts per list: the upgrade popup (free firms) + where a law firm lands
    useEffect(() => {
        if (!freeFirm && !pickLanding) return;
        makeApiCallFunc(savedTeaserApi, {},
            ({data}) => {
                if (freeFirm) setLockedSaved(data || null);
                if (pickLanding) {
                    const key = data?.openSos > 0 ? 0 : data?.openLatest > 0 ? 1 : data?.open > 0 ? 2 : 1;
                    setInnerPanel(key);
                    setLandingReady(true);
                }
            },
            () => { if (pickLanding) setLandingReady(true); }, null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freeFirm]);

    const processReqReviewCount = (data) => {
            let idWithReqReviewArr = null;
            if(data?.length !== 0 )
                {
                    idWithReqReviewArr = {}
                    data?.map((item) => {
                          let incrCounter =  1 ;
                           let statusVariable =  (item.caseStatus === "denied" && item.userId !== userDetails._id) ? "requested" : item.caseStatus;

                            if(idWithReqReviewArr[item.caseId])
                            {
                                if(idWithReqReviewArr[item.caseId][statusVariable])
                                {
                                    idWithReqReviewArr[item.caseId][statusVariable] = item.caseStatus === "denied" ? item.deniedCount : idWithReqReviewArr[item.caseId][statusVariable] + incrCounter;
                                }
                                else
                                {
                                    idWithReqReviewArr[item.caseId][statusVariable] = item.caseStatus === "denied" ? item.deniedCount : incrCounter;
                                }
                            }
                            else
                            {
                                idWithReqReviewArr[item.caseId] = { [statusVariable] : item.caseStatus === "denied" ? item.deniedCount : incrCounter}
                            }
                            return item;
                    })
                }
                return idWithReqReviewArr;

    }
    const [sortOptions, setSortOptions] = useState(JSON.stringify({"field" : "dateInfo", "order" : -1}));
    const [selectedSort, setSelectedSort] = useState("Date of incident - recent first");
    useEffect(() => {
       const key = userInfo?._id;
       if (!key || defaultsMergedFor.has(key)) return;
       defaultsMergedFor.add(key);
       // law firms start with no filters, so cases a client saved their firm on are never hidden
       if (isFirmUser) return;
       setFilters(prev => {return {...prev,
                        "caseType": userInfo?.casePref
                            ? Array.from(new Set([...(prev.caseType || []), ...userInfo.casePref]))
                            : prev.caseType,
                        "incidentState": STATE_SELECTION_ENABLED && userInfo?.statePref
                            ? Array.from(new Set([...(prev.incidentState || []), ...userInfo.statePref]))
                            : prev.incidentState,
                        "dateOfIncident": prev?.dateOfIncident ? prev?.dateOfIncident : [],
                        "adversePartyPublicCompany": prev?.adversePartyPublicCompany ? prev?.adversePartyPublicCompany : [],
                        "isClassAction": prev?.isClassAction ? prev?.isClassAction : [],
                        "aiEstimateUpperValue": prev?.aiEstimateUpperValue ? prev?.aiEstimateUpperValue : [],
                        "attAvailable": prev?.attAvailable ? prev?.attAvailable : [],
                        "aiDate": prev?.aiDate ? prev?.aiDate : [],
                        "isEntity": prev?.isEntity ? prev?.isEntity : [],
                        "clientState": prev?.clientState ? prev?.clientState : [],
                        "aiDifficulty": prev?.aiDifficulty ? prev?.aiDifficulty : [],
                        "defendant": prev?.defendant ? prev?.defendant : []}});
       setUpdateComp && setUpdateComp(prev => !prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[userInfo]);

    const canFetch = (panel) => userInfo.isActiveSubscription || userDetails?.role === FIRM || (panel !== 0 && panel !== 1);
    const getListType = (panel = innerPanel) => panel === 3 ? userListCasesApi + "requested" : listCasesApi + OPEN_TYPE[panel];
    const baseBody = (f) => {
        const body = { filters: f, userId: userInfo?._id };
        if (premiumFirm && savedOnly) body.savedOnly = true; // the server picks the firm from the signed-in account
        return body;
    };

    useEffect(() => {
        setCases(null);
        setReqRevCountArr(null);
        setTotalCases(0);
        if (!landingReady) return; // still finding which list a law firm lands on
        if(canFetch(innerPanel))
        {
          if (innerPanel !== OVERVIEW) // the Overview tab loads its own data
          {
            const postData = {
                    ...baseBody(filters),
                    sortOptions : JSON.parse(sortOptions),
                    sendCount: sendCount,
                    caseTypeCount: caseTypeCount,
                    limit: limit,
                    offset: offset,
                }
            const mine = ++fetchSeq.current;
            makeApiCallFunc(
                getListType(),
                postData,
                ({data}) =>
                {
                    if (mine !== fetchSeq.current) return;
                    setCases(data?.cases || []);
                    setTotalCases(data?.totalCount ? data?.totalCount : 0);
                    setSavedCount(typeof data?.savedCount === 'number' ? data.savedCount : null);
                    setReqRevCountArr(processReqReviewCount(data?.userCaseStatusCount))

                    if(innerPanel === 3)
                    {
                        setRequestedCount({"requested" : data?.totalCount || 0})
                    }
                },
                (data) => { console.log(data); if (mine === fetchSeq.current) setCases([]); },
                null,
                true
                )
          }
            if(innerPanel !== 3)
            {
                makeApiCallFunc(getCaseCountApi,
                    {
                        statuses: ["requested"],
                        userId: userInfo?._id
                    },
                    ({data}) =>
                    {
                        data?.map((item) => {
                            setRequestedCount(prev => ({...prev, [item._id] : item.count}))
                            return item;
                        })
                    },
                    (data) => { console.log(data)},
                    null,
                    true
                    )
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[updateComp, innerPanel, offset, userInfo, savedOnly, landingReady])

    const sendReminder = () => {
        const notificationObj = {
            title : userDetails.firstName +" "+ userDetails.lastName + " Assistant has requested you to subscribe for a plan",
            is_read : false,
            notificationCategory: 0,
            sender: userDetails._id,
            type: "1",
            receiver:  attorney?.userId?._id,
            additionalInfo:
            {
                url : "/subscription"
            },
            content: "Assistant professional "+userDetails?.firstName+" "+userDetails?.lastName+" has requested to activate a subscription plan",
            }
        makeApiCallFunc(raiseNotificationApi,
            notificationObj,
                () => toast.success("Reminder sent successfully"),
                (error) => console.log(error)
            )
        };

    const selectType = (key) => {
        setInnerPanel(key);
        writeCaseType('1', key);
        setOffset(0);
        setSendCount(key !== 3);
        setCaseTypeCount(key !== 3 ? ["requested"] : []);
    };

    useRestoreListScroll('1', cases !== null);
    const current = TYPES.find((t) => t.key === innerPanel) || TYPES[1];
    const railItems = [
        ...(overviewFirm ? [{ key: OVERVIEW, label: 'Overview', icon: 'overview', tip: 'How clients are finding your firm' }] : []),
        ...TYPES.map((t) => t.key === 3 ? { ...t, count: requestedCount?.requested ? requestedCount?.requested : 0 } : t),
    ];

    // "Show N cases" in the filter popup — same list call, only the total is used
    const countFor = (draft) => canFetch(innerPanel)
        ? { url: getListType(), body: { ...baseBody(draft), sortOptions: null, limit: 1, offset: 0 }, pick: (d) => (d?.totalCount || 0) }
        : null;
    // per-option counts next to Type of case / states (open case lists only)
    const facetsFor = (draft) => (canFetch(innerPanel) && innerPanel !== 3)
        ? { url: filterOptionCountsApi, body: { ...baseBody(draft), type: OPEN_TYPE[innerPanel] }, pick: (d) => d }
        : null;

    const toggleSaved = () => { setOffset(0); setSavedOnly((v) => !v); };
    const showSavedChip = premiumFirm && innerPanel !== 3 && (savedOnly || savedCount > 0);

    // verified free law firms only ever see cases a client saved their firm on
    const freeVerifiedFirm = userDetails?.role === FIRM && !userInfo?.isActiveSubscription && !!userInfo?.verified;
    const freeFirmEmpty = freeVerifiedFirm && innerPanel !== 3 && Array.isArray(cases) && cases.length === 0;
    // nothing to filter or sort in that case - hide them (kept when filters are
    // applied, so the firm can still change or clear them)
    const filtersApplied = Object.values(filters || {}).some((v) => Array.isArray(v) && v.length > 0);
    const hideListTools = freeFirmEmpty && !filtersApplied;
    const emptyText = savedOnly && innerPanel !== 3
        ? { title: 'No saved cases here', body: 'No client has saved your firm on a case in this list yet. Turn off the Saved filter to see every case.' }
        : { title: 'No ' + current.label.toLowerCase() + ' yet', body: 'Cases will appear here as they move into this stage. Try changing your filters.' };
    const builder = (activePanel) => {
            if (activePanel === 3) {
                return <RequestedCases attorney={attorney} cases={cases} reqRevCountArr={reqRevCountArr} setUpdateComp={setUpdateComp} emptyText={emptyText}/>
            }
            return <AllCases attorney={attorney} reqRevCountArr={reqRevCountArr} cases={cases} setUpdateComp={setUpdateComp} emptyText={emptyText}/>
    }

  return (
    <CaseListLayout
        attorney={attorney}
        sectionKey="1"
        railItems={railItems}
        activeKey={innerPanel}
        onSelect={selectType}
        setUpdateComp={setUpdateComp}
    >
        {innerPanel === OVERVIEW ? <FirmOverview mode="free" onShowRequested={() => { selectType(3); window.scrollTo({ top: 0 }); }}/> : <>
        {!hideListTools && <FilterBar attorney={attorney} setUpdateComp={setUpdateComp} countFor={countFor} facetsFor={facetsFor}/>}
        {canFetch(innerPanel) ?
        <>
            <ResultLine
                label={current.label}
                total={totalCases}
                loading={!cases}
                right={
                    <div className="flex items-center gap-2 flex-none">
                        {showSavedChip && <SavedChip active={savedOnly} count={savedCount} onClick={toggleSaved}/>}
                        {!hideListTools && <SortingOptions setOffset={setOffset} setUpdateComp={setUpdateComp} sortOptions={sortOptions} setSortOptions={setSortOptions} selectedSort={selectedSort} setSelectedSort={setSelectedSort}/>}
                    </div>
                }
            />
            {/* verified free law firm with no saved cases in this list: no empty state,
                only the blurred sample cases with the upgrade message */}
            {freeFirmEmpty
                ? <FirmUpgradeCard
                    empty
                    label={current.label}
                    assistant={userDetails.isUserAssistant}
                    onUpgrade={() => navigate('/attorney/subscription')}
                  />
                : cases ? builder(innerPanel) : <CardSkeleton/>}
            {!freeFirmEmpty && <CasePager
                offset={offset}
                limit={limit}
                total={isVerified ? totalCases : offset + (cases?.length || 0)}
                shown={cases?.length || 0}
                onPage={(next) => setOffset(next)}
            />}
            {userDetails?.role === FIRM && !userInfo?.isActiveSubscription && cases && !freeFirmEmpty &&
                (innerPanel === 0 || innerPanel === 1) &&
                <FirmUpgradeCard
                    label={current.label}
                    assistant={userDetails.isUserAssistant}
                    onUpgrade={() => navigate('/attorney/subscription')}
                />}
        </>
        :
        <PremiumGate
            label={current.label}
            assistant={userDetails.isUserAssistant}
            onUpgrade={() => navigate('/attorney/subscription')}
            onRemind={sendReminder}
            savedCount={lockedSaved?.[OPEN_TYPE[innerPanel]] || 0}
        />
        }
        </>}
    </CaseListLayout>
  )
}

// Premium law firms: show only the cases where a client saved this firm
function SavedChip({ active, count, onClick }) {
    const isMobile = useIsMobile();
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            title={active ? 'Showing only cases where a client saved your firm. Click to see every case.' : 'Show only cases where a client saved your firm'}
            className={`flex items-center gap-1.5 font-plexsans font-semibold text-[12.5px] leading-none rounded-md py-2 px-3 whitespace-nowrap border transition-colors cursor-pointer ${active ? 'text-ivory bg-brass border-brass hover:bg-brassDark' : 'text-brass bg-white border-brass/[.4] hover:bg-brass/[.06]'}`}
        >
            <svg width="12" height="12" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="flex-none"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
            {isMobile ? 'Saved' : 'Saved your firm'}
            {typeof count === 'number' && <span className={active ? 'text-ivory/[.8]' : 'text-brass/[.7]'}>· {count}</span>}
        </button>
    );
}

// Free law firms on SOS / Cases just posted see only the cases a client saved
// their firm on. Under those: made-up sample cases, fully blurred, with the
// message that the rest of the list opens with Premium. When a verified free
// firm has no saved cases in a list (Available cases too), this replaces the
// empty state ("empty").
function FirmUpgradeCard({ label, assistant, onUpgrade, empty = false }) {
    const what = label.toLowerCase().replace(/^sos/, 'SOS');
    return (
        <div className={`relative ${empty ? '' : 'mt-5'}`}>
            <BlurredSampleCases count={2} />
            <div className="absolute inset-0 flex items-center justify-center px-4">
                <div className="max-w-[440px] w-full bg-ivory border border-brass/[.35] rounded-lg shadow-[0_24px_60px_-20px_rgba(20,33,61,.45)] pt-6 px-6 pb-6 text-center font-plexsans">
                    <div className="font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-brass mb-2.5">Premium</div>
                    <div className="font-plexsans font-semibold text-[17px] leading-[1.35] text-navy mb-2">{empty ? 'No saved cases here yet' : `See all ${what}`}</div>
                    <div className="font-plexsans text-[13.5px] leading-[1.55] text-navy/[.6] mb-[18px]">
                        {empty
                            ? `No client has saved your firm on any of the ${what} yet. Upgrade to Premium to see all existing ${what}.`
                            : `You're seeing only the cases where a client saved your firm. Upgrade to Premium to see all existing ${what}.`}
                    </div>
                    {assistant
                        ? <div className="font-plexsans text-[12.5px] text-navy/[.55]">The firm account owner can upgrade to Premium.</div>
                        : <CaseBtn onClick={onUpgrade}>Upgrade to Premium</CaseBtn>}
                </div>
            </div>
        </div>
    );
}

// SOS / just-posted cases are a Premium feature
function PremiumGate({ label, assistant, onUpgrade, onRemind, savedCount = 0 }) {
    return (
        <div className="relative">
            <div aria-hidden className="blur-[3px] pointer-events-none select-none opacity-80">
                <CardSkeleton count={2}/>
            </div>
            <div className="absolute inset-0 flex items-start justify-center pt-[70px]">
                <div className="max-w-[420px] my-0 mx-4 bg-ivory border border-brass/[.35] rounded-lg shadow-[0_24px_60px_-20px_rgba(20,33,61,.45)] pt-[26px] px-[26px] pb-6 text-center font-plexsans">
                    <div className="font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-brass mb-2.5">Premium</div>
                    <div className="font-plexsans font-semibold text-[17px] leading-[1.35] text-navy mb-2">{label} come with Premium</div>
                    {savedCount > 0 && !assistant && (
                        <div className="flex items-center justify-center gap-2 mb-3 py-2.5 px-3 rounded-md bg-brass/[.08] border border-brass/[.3] font-plexsans font-semibold text-[13.5px] leading-[1.4] text-brass">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" className="flex-none"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                            {savedCount === 1 ? 'A client saved your firm' : `${savedCount} clients saved your firm`} on {label.replace(/^Cases/, 'cases')}
                        </div>
                    )}
                    <div className="font-plexsans font-normal text-[13.5px] leading-[1.55] text-navy/[.6] mb-[18px]">
                        {assistant
                            ? 'The attorney needs an active Premium membership for you to see these cases.'
                            : savedCount > 0
                                ? `Upgrade to see ${savedCount === 1 ? 'it' : 'them'}, plus early access to new and urgent cases, AI case values and premium filters.`
                                : 'Get early access to new and urgent cases, AI case values and premium filters.'}
                    </div>
                    {assistant
                        ? <CaseBtn onClick={onRemind}>Ask the attorney to subscribe</CaseBtn>
                        : <CaseBtn onClick={onUpgrade}>Upgrade to Premium</CaseBtn>}
                </div>
            </div>
        </div>
    );
}

export default OpenCases
